/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { createContext, useContext, useEffect, useState, useId, useRef } from "react";
import styled, { css } from "styled-components";
import Button from "./Button";
import Icon from "./Icon";
import Title from "./Title";
import { WIDGET_CONTAINER_NAME } from "../../constants/constants";
import { useTranslate } from "../../hooks/useTranslate";

const StyledModalDialog = styled.dialog`
  container-name: ${WIDGET_CONTAINER_NAME};
  container-type: inline-size;
  outline: none;

  &:not([open]) {
    display: none;
  }

  position: fixed;
  inset: 0;
  margin: auto;
  padding: 0;
  border: none;
  max-width: 620px;
  width: 100%;
  max-height: calc(100dvh - 4rem);
  overflow-y: auto;
  scroll-padding-block: 4px;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding-bottom: 1.5rem;
  border-radius: 1rem;
  box-shadow: 2px 2px 15px 0px rgba(${({ theme }) => theme.colors.text.rgb}, 0.1),
              0 0 0 100vmax rgba(${({ theme }) => theme.colors.text.rgb}, 0.6);
  background: rgba(${({ theme }) => theme.colors.surface.rgba});

  opacity: 1;
  transform: scale(1);
  transition: opacity 0.2s cubic-bezier(0.16, 1, 0.3, 1),
              transform 0.2s cubic-bezier(0.16, 1, 0.3, 1);

  @starting-style {
    opacity: 0;
    transform: scale(0.97);
  }

  &::backdrop {
    background: transparent;
  }

  ${({ theme }) => theme.containerMedia.sm} {
    width: calc(100dvw - 32px);
  } 

  ${({ $placement, $width }) => $placement === "right" && css`
    margin: 0;
    margin-left: auto;
    max-width: 100%;
    width: ${$width || "620px"};
    height: 100dvh;
    max-height: 100dvh;
    border-radius: 0;
    padding-bottom: 0;
    overflow: hidden;
    overscroll-behavior: contain;

    transform: translateX(0);
    transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1);

    @starting-style {
      transform: translateX(100%);
      opacity: 1;
    }
  `}
`;

const StyledModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-inline: 1.5rem;
  margin-top: 1.5rem;
`;

const StyledModalCloseButtonFloated = styled.div`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  z-index: 1;
`;

const StyledModalCloseButton = styled(Button)`
  flex-shrink: 0;
`;

const StyledModalImage = styled.img`
  width: 100%;
  padding: 0.5rem 0.5rem 0 0.5rem;
  border-radius: 1rem;
  object-fit: cover;
  max-height: 500px;
`;

const StyledModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-inline: 1.5rem;
`;

const StyledModalFooter = styled.div`
  padding-inline: 1.5rem;
`;

const ModalContext = createContext();

const ModalProvider = ({ children, onOpen, onClose, isOpen }) => {
  const titleId = useId();
  const [hasTitle, setHasTitle] = useState(false);
  const [isModalOpen, setModalIsOpen] = useState(isOpen);
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);

  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen);
    setModalIsOpen(isOpen);
  }

  // showModal() inerts the background for interaction but does not stop the
  // host page from scrolling behind the dialog, so the lock is still ours.
  useEffect(() => {
    if (!isModalOpen) return undefined;

    const { overflow, overscrollBehavior, position, margin } = document.body.style;

    document.body.style.overflow = "hidden";
    document.body.style.overscrollBehavior = "contain";
    document.body.style.position = "relative";
    document.body.style.margin = "0";

    return () => {
      document.body.style.overflow = overflow;
      document.body.style.overscrollBehavior = overscrollBehavior;
      document.body.style.position = position;
      document.body.style.margin = margin;
    };
  }, [isModalOpen]);

  const openModal = () => {
    setModalIsOpen(true);
    onOpen?.(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    onClose?.(false);
  };

  return (
    <ModalContext.Provider value={{ isModalOpen, openModal, closeModal, onOpen, onClose, titleId, hasTitle, setHasTitle }}>
      {children}
    </ModalContext.Provider>
  );
};

const useModal = () => {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error("useModal must be used within a ModalProvider");
  }
  return context;
};

const ModalTrigger = ({ children }) => {
  const { openModal } = useModal();

  return React.cloneElement(children, {
    onClick: (e) => {
      e.stopPropagation();

      if (children.props.onClick) {
        children.props.onClick(e);
      }

      openModal();
    },
  });
};

const ModalClose = ({ children, waitForOnClick }) => {
  const { closeModal } = useModal();

  return React.cloneElement(children, {
    onClick: async (e) => {
      if (waitForOnClick) {
        await children.props.onClick(e);
      } else if (children.props.onClick) {
        children.props.onClick(e);
      }

      closeModal();
    },
  });
};

const ModalCloseButton = () => {
  const { closeModal } = useModal();
  const { _t } = useTranslate();

  return (
    <StyledModalCloseButtonFloated>
      <Button size="icon" variant="primary" icon={<Icon.Cross />} aria-label={_t("Close")} onClick={closeModal} />
    </StyledModalCloseButtonFloated>
  );
};

const ModalImage = ({ src, alt }) => (
  <StyledModalImage src={src} alt={alt} />
);

const ModalTitle = ({ children }) => {
  const { titleId, setHasTitle } = useModal();

  useEffect(() => {
    setHasTitle(true);
    return () => setHasTitle(false);
  }, [setHasTitle]);

  return <Title id={titleId} size="h3">{children}</Title>;
};

const ModalHeader = ({ children, showClose = true }) => {
  const { closeModal } = useModal();
  const { _t } = useTranslate();

  return (
    <StyledModalHeader>
      {children}
      {showClose && <StyledModalCloseButton size="icon" variant="transparent" icon={<Icon.Cross />} aria-label={_t("Close")} onClick={closeModal} />}
    </StyledModalHeader>
  );
};

const ModalBody = ({ children, ...props }) => {
  return <StyledModalBody {...props}>{children}</StyledModalBody>;
};

const ModalFooter = ({ children }) => {
  return <StyledModalFooter>{children}</StyledModalFooter>;
};

const ModalContent = ({ children, placement, width }) => {
  const { isModalOpen, closeModal, titleId, hasTitle } = useModal();
  const { _t } = useTranslate();
  const dialogRef = useRef(null);

  // Read by the native listener below so it binds once and still sees current
  // values, instead of resubscribing on every render.
  const latestRef = useRef({ isModalOpen, closeModal });
  useEffect(() => {
    latestRef.current = { isModalOpen, closeModal };
  });

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;

    // Esc closes a <dialog> natively — mirror that back into React state.
    // Bound natively rather than via React's onCancel/onClose, which would
    // depend on styled-components forwarding those props to the DOM node.
    // Skipped when we closed it ourselves, so onClose fires once, not twice.
    const handleNativeClose = () => {
      if (latestRef.current.isModalOpen) latestRef.current.closeModal();
    };

    dialog.addEventListener("close", handleNativeClose);
    return () => dialog.removeEventListener("close", handleNativeClose);
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isModalOpen && !dialog.open) {
      dialog.showModal();
      dialog.focus({ preventScroll: true });
    } else if (!isModalOpen && dialog.open) {
      dialog.close();
    }
  }, [isModalOpen]);

  return (
    <StyledModalDialog
      ref={dialogRef}
      tabIndex={-1}
      aria-labelledby={hasTitle ? titleId : undefined}
      aria-label={hasTitle ? undefined : _t("Dialog")}
      $placement={placement}
      $width={width}
      onClick={(e) => {
        // A click on ::backdrop reports the dialog as its target — but so does
        // a click on the dialog's own padding or column gap. Compare against
        // the box so only true backdrop clicks close.
        if (e.target !== dialogRef.current) return;

        const { top, right, bottom, left } = dialogRef.current.getBoundingClientRect();
        const isOnBackdrop =
          e.clientX < left || e.clientX > right || e.clientY < top || e.clientY > bottom;

        if (isOnBackdrop) closeModal();
      }}
    >
      {isModalOpen && children}
    </StyledModalDialog>
  );
};

const Modal = ({ isOpen, onOpen, onClose, children }) => {
  return (
    <ModalProvider onOpen={onOpen} onClose={onClose} isOpen={isOpen}>
      {children}
    </ModalProvider>
  );
};

export { Modal, ModalTrigger, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalClose, ModalCloseButton, ModalImage, ModalTitle };
